import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPriceWithDollar } from 'utils/currency';
import { formatDateForDisplay } from 'utils/dateUtils';

/**
 * Generate Worksheet PDF using jsPDF
 * Returns { blob, blobUrl, filename }
 */
export const generateWorksheetPDF = (worksheet) => {
  const doc = new jsPDF();
  
  // Helper function to call autoTable
  const addTable = (options) => autoTable(doc, options);
  const vehicle = worksheet.inventory?.inventory_vehicle || {};
  const financeCompany = worksheet.finance_company || {};
  const tradeIns = worksheet.trade_in || [];
  const defDowns = worksheet.def_downs || [];
  const schedules = worksheet.schedules || [];
  const afterMarket = worksheet.after_market || [];
  const accessories = worksheet.accessories || [];
  const commissions = worksheet.commissions || {};
  const financeCompanyData = worksheet.finance_company || {};
  const washout = worksheet.washout || {};

  const toPrice = (val) => formatPriceWithDollar(val || 0);
  const toPercent = (val) => val ? `${parseFloat(val).toFixed(4)}%` : '0.0000%';

  // Generate UUID filename
  const uuid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const filename = `${uuid}.pdf`;

  let y = 10;

  // Date at top left
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Date:', 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDateForDisplay(new Date()), 25, y);
  y += 8;

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('WORKSHEET', 105, y, { align: 'center' });
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Worksheet ID: ${worksheet.worksheet_id} | Type: ${(worksheet.type || '').replace(/_/g, ' ').toUpperCase()}`, 105, y, { align: 'center' });
  y += 15;

  // VEHICLES IN DEAL
  if (worksheet.inventory) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('VEHICLES IN DEAL', 10, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${vehicle.year} ${vehicle.make} ${vehicle.model}`, 10, y);
    y += 5;

    const vehicleInfo = [];
    if (worksheet.inventory.vin) vehicleInfo.push(['VIN:', worksheet.inventory.vin]);
    if (worksheet.inventory.stock_number) vehicleInfo.push(['Stock #:', worksheet.inventory.stock_number]);
    if (vehicle.mileage || worksheet.inventory.actual_mileage) vehicleInfo.push(['Mileage:', vehicle.mileage || worksheet.inventory.actual_mileage]);
    if (vehicle.exterior_color) vehicleInfo.push(['Color:', vehicle.exterior_color]);
    if (vehicle.status) vehicleInfo.push(['Status:', vehicle.status]);

    if (vehicleInfo.length > 0) {
      addTable({
        startY: y,
        body: vehicleInfo,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 1 },
        columnStyles: { 0: { fontStyle: 'bold' } },
      });
      y = doc.lastAutoTable.finalY + 10;
    }
  }

  // WASHOUT SUMMARY
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('WASHOUT SUMMARY', 10, y);
  y += 6;

  const washoutData = [
    ['APR:', toPercent(washout?.apr || worksheet.loan_rate), 'Total Down:', toPrice(washout?.total_down || ((worksheet.deposit || 0) + defDowns.reduce((sum, d) => sum + (d.amount || 0), 0)))],
    ['Finance Charge:', toPrice(washout?.finance_charge), 'Total Sale:', toPrice(washout?.total_sale || worksheet.total_sale)],
    ['Amount Financed:', toPrice(washout?.amount_financed), 'Total Payments:', toPrice(washout?.total_payments || (washout?.amount_financed && washout?.finance_charge ? washout.amount_financed + washout.finance_charge : 0))],
  ];

  addTable({
    startY: y,
    body: washoutData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // SALES SUMMARY
  const feesTotal = Array.isArray(worksheet.fees) ? worksheet.fees.reduce((sum, f) => sum + (f.amount || 0), 0) : 0;
  const taxesTotal = Array.isArray(worksheet.taxes) ? worksheet.taxes.reduce((sum, t) => sum + (t.amount || 0), 0) : 0;
  const afterMarketTotal = afterMarket.reduce((sum, item) => sum + (item.price || 0), 0);
  const accessoriesTotal = accessories.reduce((sum, item) => sum + (item.price || 0), 0);
  const totalDown = (worksheet.deposit || 0) + defDowns.reduce((sum, d) => sum + (d.amount || 0), 0);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('SALES SUMMARY', 10, y);
  y += 6;

  const salesData = [
    ['Sales Price:', toPrice(worksheet.sales_price)],
    ['Fees:', toPrice(feesTotal)],
    ['Taxes:', toPrice(taxesTotal)],
    ['After Market:', toPrice(afterMarketTotal)],
    ['Accessories:', toPrice(accessoriesTotal)],
    ['Total Down:', toPrice(totalDown)],
    ['Total Sale:', toPrice(worksheet.total_sale)],
  ];

  addTable({
    startY: y,
    body: salesData,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold' } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // FINANCE SUMMARY
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('FINANCE SUMMARY', 10, y);
  y += 6;

  const totalPayments = washout?.total_payments || (washout?.amount_financed && washout?.finance_charge ? washout.amount_financed + washout.finance_charge : 0);
  const financeData = [
    ['Total Sale:', toPrice(washout?.total_sale || worksheet.total_sale)],
    ['Total Down:', toPrice(washout?.total_down || totalDown)],
    ['Amount Financed:', toPrice(washout?.amount_financed)],
    ['Finance Charge:', toPrice(washout?.finance_charge)],
    ['APR:', toPercent(washout?.apr || worksheet.loan_rate)],
    ['Total Payments:', toPrice(totalPayments)],
  ];

  addTable({
    startY: y,
    body: financeData,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold' } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // CONTRACT DETAIL
  if (y > 250) { doc.addPage(); y = 10; }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTRACT DETAIL', 10, y);
  y += 6;

  if (financeCompany.name) {
    doc.setFontSize(10);
    doc.text(`LENDER: ${financeCompany.name}`, 10, y);
    y += 5;
  }

  const contractLeft = [
    ['Contract Date:', formatDateForDisplay(worksheet.contract_date)],
    ['Sales Price:', toPrice(worksheet.sales_price)],
    ['Initial Cash Down:', toPrice(worksheet.initial_cash_down || worksheet.deposit)],
    ['Loan Rate:', toPercent(worksheet.loan_rate)],
  ];
  if (worksheet.use_max_rate) contractLeft.push(['Use Max Rate:', 'Yes']);

  let contractRight = [];
  if (schedules.length > 0) {
    contractRight = [
      ['Schedule 1:', toPrice(schedules[0].desired_payment)],
      ['# of Payments:', String(schedules[0].number_of_payments)],
      ['Frequency:', schedules[0].frequency || '-'],
      ['First Payment:', formatDateForDisplay(schedules[0].first_payment_date)],
      ['Final Payment Due:', formatDateForDisplay(schedules[0].final_payment_due_on)],
      ['Final Payment:', toPrice(schedules[0].final_payment_amount)],
    ];
  }

  // Merge left and right
  const maxLen = Math.max(contractLeft.length, contractRight.length);
  const contractData = [];
  for (let i = 0; i < maxLen; i++) {
    const left = contractLeft[i] || ['', ''];
    const right = contractRight[i] || ['', ''];
    contractData.push([...left, ...right]);
  }

  addTable({
    startY: y,
    body: contractData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // DEF DOWNS
  if (defDowns.length > 0 && defDowns.some(d => d.amount)) {
    if (y > 260) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DEF DOWNS', 10, y);
    y += 6;

    const defDownsData = defDowns.map((def, idx) => [idx + 1, toPrice(def.amount), formatDateForDisplay(def.due_on)]);

    addTable({
      startY: y,
      head: [['#', 'Amount', 'Due On']],
      body: defDownsData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // TRADE-IN
  if (tradeIns.length > 0 && tradeIns.some(t => t.trade_amount || t.acv || t.payoff)) {
    if (y > 250) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('TRADE-IN', 10, y);
    y += 6;

    const tradeData = tradeIns.map(trade => [
      toPrice(trade.trade_amount),
      toPrice(trade.acv),
      toPrice(trade.payoff),
      toPrice(trade.cash_back),
    ]);
    tradeData.push([
      'Total',
      toPrice(tradeIns.reduce((sum, t) => sum + (t.acv || 0), 0)),
      toPrice(tradeIns.reduce((sum, t) => sum + (t.payoff || 0), 0)),
      toPrice(tradeIns.reduce((sum, t) => sum + (t.cash_back || 0), 0)),
    ]);

    addTable({
      startY: y,
      head: [['Trade Value', 'ACV', 'Payoff', 'Cash Back']],
      body: tradeData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // AFTER MARKET
  if (afterMarket.length > 0) {
    if (y > 260) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('AFTER MARKET', 10, y);
    y += 6;

    addTable({
      startY: y,
      head: [['Name', 'Price', 'Cost']],
      body: afterMarket.map(item => [item.name, toPrice(item.price), toPrice(item.cost)]),
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ACCESSORIES
  if (accessories.length > 0) {
    if (y > 260) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('ACCESSORIES', 10, y);
    y += 6;

    addTable({
      startY: y,
      head: [['Name', 'Price', 'Cost']],
      body: accessories.map(item => [item.name, toPrice(item.price), toPrice(item.cost)]),
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // SERVICE CONTRACT
  if (worksheet.service_contract && worksheet.service_contract_fields) {
    if (y > 250) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SERVICE CONTRACT', 10, y);
    y += 6;

    const sc = worksheet.service_contract_fields;
    const scData = [
      ['Price:', toPrice(sc.price), 'Term:', sc.term || '-'],
      ['Cost:', toPrice(sc.cost), 'Mileage:', sc.mileage || '-'],
      ['Company:', sc.company || '-', 'Code:', sc.code || '-'],
    ];

    addTable({
      startY: y,
      body: scData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // GAP INSURANCE
  if (worksheet.gap_insurance && worksheet.gap_insurance_fields) {
    if (y > 250) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('GAP INSURANCE', 10, y);
    y += 6;

    const gap = worksheet.gap_insurance_fields;
    const gapData = [
      ['Price:', toPrice(gap.price), 'Term:', gap.term || '-'],
      ['Cost:', toPrice(gap.cost), 'Mileage:', gap.mileage || '-'],
      ['Company:', gap.company || '-', 'Code:', gap.code || '-'],
    ];

    addTable({
      startY: y,
      body: gapData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // LENDER FEES & DECISION
  if (y > 230) { doc.addPage(); y = 10; }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('LENDER FEES & DECISION', 10, y);
  y += 6;

  const lenderData = [
    ['Discount:', worksheet.use_discount ? 'Yes' : 'No', 'Buy Rate %:', `${financeCompanyData.buy_rate_percent || 0}%`],
    ['Use Reserve:', worksheet.use_reserve ? 'Yes' : 'No', 'Flat Reserve:', toPrice(financeCompanyData.flat_reserve)],
    ['Escrow Fee:', toPrice(financeCompanyData.escrow_fee), 'Dealer Reserve:', toPrice(financeCompanyData.dealer_reserve)],
    ['Loan Acq Fee:', toPrice(financeCompanyData.loan_acq_fee), 'DLR Reserve Retain %:', `${financeCompanyData.dlr_reserve_retain_percent || 0}%`],
    ['Flat Discount:', toPrice(financeCompanyData.flat_discount)],
    ['Discount %:', `${financeCompanyData.discount_percent || 0}%`],
  ];

  addTable({
    startY: y,
    body: lenderData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // COMMISSIONS
  if (Object.keys(commissions).length > 0) {
    if (y > 240) { doc.addPage(); y = 10; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('COMMISSIONS', 10, y);
    y += 6;

    const commData = [
      ['Finder Fee:', toPrice(commissions.finder_fee), 'Down Pay %:', `${commissions.down_pay_percent || 0}%`],
      ['Sales Gross %:', `${commissions.sales_gross_percent || 0}%`, 'Def Down Pay %:', `${commissions.def_down_pay_comm_percent || 0}%`],
      ['Comm Gross %:', `${commissions.comm_gross_percent || 0}%`, 'Dealer Reserve %:', `${commissions.dealer_reserve_comm_percent || 0}%`],
      ['Front End %:', `${commissions.front_end_percent || 0}%`, 'Flat Commission:', toPrice(commissions.flat_commission)],
      ['Back End %:', `${commissions.back_end_percent || 0}%`, 'Pack Overhead:', toPrice(commissions.pack_overhead)],
    ];

    addTable({
      startY: y,
      body: commData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 15;
  }

  // SIGNATURES
  if (y > 260) { doc.addPage(); y = 10; }

  // Generate blob and URL
  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);

  // Auto-download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return { blob: pdfBlob, blobUrl, filename };
};

export default generateWorksheetPDF;
